/**
 * ALMACÉN LOCAL CIFRADO DEL PAQUETE OFFLINE (FASE 5.1-5.5) + CIFRADO EN
 * REPOSO POR PERFIL (FASE 5.6)
 *
 * UNA BASE POR PERFIL, NUNCA POR USUARIO (§7/§18)
 *
 * Hasta 5.5 la base física se llamaba `neatos-offline-{idUsuario}` — desde
 * 5.6 se llama `neatos-offline-cripto-{perfilId}` (`perfil.ts::
 * nombreBaseDeDatosDePerfil`), con `perfilId` un UUID LOCAL opaco que
 * nunca es el identificador real de negocio. El parámetro que reciben
 * todas las funciones de este fichero sigue llamándose `idPerfil` por la
 * misma razón que antes (encargo §18: "el aislamiento debe existir en la
 * capa IndexedDB, no depender sólo de filtros") — pero ahora la propiedad
 * es doble: bases FÍSICAMENTE distintas (como antes) Y, dentro de cada
 * fila, un `AAD` (`offline/crypto.ts`) que liga el ciphertext a este
 * `perfilId` concreto. Un ciphertext copiado de un perfil a otro por error
 * de programación falla a descifrar SIEMPRE, aunque alguien lograra
 * apuntar por accidente a la base equivocada.
 *
 * CIFRADO EN EL LÍMITE DEL ALMACÉN, NO EN CADA LLAMADA (§19/§20/§25)
 *
 * Cada store guarda SÓLO los campos que hacen falta como `keyPath`/índice
 * IndexedDB en claro (nunca contenido de negocio — ver la lista exacta en
 * cada función `crear...Store` de `abrirDb`), más un campo `cifrado`
 * (`EnvelopeCifrado`) que contiene el objeto lógico COMPLETO (incluidos
 * los campos que también están en claro — la redundancia es barata y
 * evita tener que fusionar dos mitades del mismo registro en cada
 * lectura). Toda función exportada de este fichero sigue devolviendo
 * exactamente el mismo tipo lógico que en FASE 5.1-5.5 (`OtOfflineLocal`,
 * `Mutacion`, etc.) — quien llama (`mutaciones.ts`, `sync.ts`,
 * `paquete.ts`, las páginas) no necesita saber que existe cifrado.
 *
 * TODA FUNCIÓN EXIGE UN PERFIL DESBLOQUEADO
 *
 * `dekActivaOLanzar(idPerfil)` (`perfil.ts`) es lo PRIMERO que hace cada
 * función exportada. Si el perfil no está desbloqueado —o `idPerfil` no
 * coincide con el que de verdad está activo—, lanza antes de tocar
 * IndexedDB. Es la aplicación estructural de §17 ("si LOCKED: NO sync") y
 * §11 ("perfil bloqueado: cero mutaciones/lecturas"): ningún llamador
 * puede olvidarlo, porque no hay forma de leer/escribir sin pasar por
 * aquí.
 *
 * ESQUEMA NUEVO, SIN MIGRACIÓN DE VERSIÓN (§23/§25)
 *
 * `neatos-offline-cripto-{perfilId}` es SIEMPRE una base nueva la primera
 * vez que se abre (un `perfilId` se genera una única vez, en
 * `crearPerfilLocal`) — por eso `onupgradeneeded` no necesita ninguna
 * lógica de "si ya existía, conserva" como tenían las migraciones 5.1→5.2
 * →5.3→5.4→5.5: no hay ninguna versión anterior de ESTA base que
 * conservar. La migración de verdad —qué hacer con una base LEGACY
 * `neatos-offline-{idUsuario}` de antes de 5.6— vive aparte, en
 * `offline/legado.ts`, precisamente para no mezclar "cómo se abre la base
 * nueva" con "qué hacer con la vieja" (§23: "no intentar migración
 * async compleja dentro de onupgradeneeded").
 */
import type { PaqueteDocumentoOffline, PaqueteLineaOffline, PaqueteMantenimiento, PaqueteOffline, PaqueteOtOffline, ParteCampo } from '../api.ts';
import { cifrarBlob, cifrarRegistro, descifrarBlob, descifrarRegistro, type EnvelopeCifrado } from './crypto.ts';
import { avisarCambioCola } from './operaciones.ts';
import { dekActivaOLanzar, nombreBaseDeDatosDePerfil } from './perfil.ts';

const VERSION_ESQUEMA = 1;
const CLAVE_META = 'actual';
/** F14.3 — el paquete del técnico vive en el MISMO store `meta`, en su propia fila cifrada: sin cambio de esquema. */
const CLAVE_MANTENIMIENTO = 'mantenimiento';

export interface MetaOffline {
  readonly clave: 'actual';
  readonly rol: 'ENCARGADO' | 'OPERARIO' | 'TECNICO';
  readonly generadoEn: string;
  readonly ultimaSincronizacionEn: string;
  readonly validoOperativamenteHasta: string;
  /** F14.2 — desde aquí no se empieza trabajo nuevo con estos datos (`caducidad.ts`). Ausente en paquetes anteriores. */
  readonly bloqueaTrabajoNuevoDesde?: string | null;
  readonly ventanaDesde: string;
  readonly ventanaHasta: string;
  readonly centros: readonly string[];
  readonly totalOts: number;
  readonly versionEsquemaLocal: number;
  /** Contador monotónico del dispositivo (FASE 5.2 §4) — nunca `Date.now()`. */
  readonly ultimoOrdenLocal: number;
}

export interface DocumentoOfflineLocal extends PaqueteDocumentoOffline {
  readonly disponibleOffline: boolean;
  /**
   * F14.3 — documento de mantenimiento (`ambito: 'PT'`): el id de la evidencia en el servidor, con el que se descarga
   * (`/api/mantenimiento/evidencias/:id/archivo`). `id` es entonces `idPt:idEvidencia` (el mismo documento del activo
   * puede ir en dos partes). Ausente en Limpieza.
   */
  readonly idEvidenciaMnt?: string;
}

/** F14.3 — un parte del paquete del técnico; `fueraDeVentana` igual que en las OT (§18 FASE 5.2). */
export interface ParteCampoLocal extends ParteCampo {
  readonly fueraDeVentana?: boolean;
}

/** F14.3 — lo que se guarda del paquete del técnico (los documentos van al store `documentos`, como en Limpieza). */
export interface MantenimientoLocal {
  readonly clave: 'mantenimiento';
  readonly partes: readonly ParteCampoLocal[];
  readonly stock: PaqueteMantenimiento['stock'];
}

/** `fueraDeVentana: true` = ya no viene en el último paquete del servidor; se conserva sólo porque tiene mutaciones pendientes (§18, FASE 5.2). Ausente/false en el caso normal. */
export interface OtOfflineLocal extends PaqueteOtOffline {
  readonly fueraDeVentana?: boolean;
}

export interface LineaOfflineLocal extends PaqueteLineaOffline {
  readonly fueraDeVentana?: boolean;
}

// ------------------------------------------------------- mutaciones (5.2 + 5.4)

export type TipoMutacionLinea = 'LINEA_CONFIRMAR' | 'LINEA_VERIFICAR' | 'LINEA_NO_OK' | 'LINEA_NO_APLICA';
/** FASE 5.4 §3 — NC_ANULAR queda fuera a propósito (mismo criterio que el backend, `sync/tipos.ts`). */
export type TipoMutacionNc = 'NC_CREAR' | 'NC_INICIAR_TRATAMIENTO' | 'NC_CERRAR';
/** F14.3 — mantenimiento del técnico (`offline/operaciones-mantenimiento.ts`); mismo `POST /api/sync`. */
export type TipoMutacionMnt = 'MNT_INICIAR' | 'MNT_NOTA' | 'MNT_CONSUMO' | 'MNT_FINALIZAR';
/** `EVIDENCIA_SUBIR` NUNCA viaja por `/api/sync` (JSON) — es multipart, ver `offline/sync.ts` §10. Vive en la MISMA cola por uniformidad de UI (una lista, un botón "sincronizar"), no porque comparta transporte. */
export type TipoMutacion = TipoMutacionLinea | TipoMutacionNc | TipoMutacionMnt | 'EVIDENCIA_SUBIR';

export type EstadoSyncMutacion =
  | 'PENDIENTE'
  | 'SINCRONIZANDO'
  | 'SINCRONIZADA'
  | 'EN_ESPERA'
  | 'ERROR_REINTENTABLE'
  | 'RECHAZADA';

/** Mismo contrato que `DatosConfirmarLinea` (`ejecucion/tipos.ts`, backend) — nunca un payload "de UI". */
export interface PayloadLineaConfirmar {
  readonly fechaEfectiva: string;
  readonly observacion: string | null;
  readonly ejecutoresReales: readonly string[];
}

/** Mismo contrato que `DatosAccionLinea`. */
export interface PayloadLineaVerificar {
  readonly fechaEfectiva: string;
  readonly observacion: string | null;
}

/** Mismo contrato que `DatosMotivoLinea` (NO_OK y NO_APLICA). */
export interface PayloadLineaMotivo {
  readonly fechaEfectiva: string;
  readonly observacion: string | null;
  readonly motivo: string;
}

/** Mismo contrato que `DatosCrearNc` (backend `no-conformidades/tipos.ts`), sin `idOt`/`idOperacionCliente` — esos ya son campos propios de `Mutacion`. */
export interface PayloadNcCrear {
  readonly descripcion: string;
  readonly gravedad: 'LEVE' | 'GRAVE' | 'CRITICA';
  readonly idsLineas?: readonly string[];
}

/** Mismo contrato que `DatosIniciarTratamiento`. */
export interface PayloadNcIniciarTratamiento {
  readonly motivo: string | null;
}

/** Mismo contrato que `DatosCerrarNc`. */
export interface PayloadNcCerrar {
  readonly accionCorrectiva: string;
}

/**
 * Metadatos de una evidencia pendiente de subir — el fichero en sí NUNCA
 * vive aquí (el payload de una `Mutacion` se lee entero en cada operación
 * de `db.ts`; guardar el blob aquí lo duplicaría en cada lectura). Vive en
 * `evidenciaBlobs`, indexado por el mismo `idOperacionCliente` — ver
 * `guardarBlobEvidencia`/`leerBlobEvidencia` más abajo.
 */
export interface PayloadEvidenciaSubir {
  /** F14.3 — `PT`: foto de una intervención de mantenimiento (`idEntidad` = id del parte). */
  readonly ambito: 'OT' | 'LINEA' | 'NC' | 'PT';
  readonly contexto: string;
  readonly descripcion: string | null;
  readonly tomadaEn: string | null;
  readonly visibilidad: 'INTERNA' | 'CLIENTE';
  readonly hashSha256: string;
  readonly mime: string;
  readonly tamanoBytes: number;
  readonly nombreArchivo: string;
  /**
   * F14.2 — foto del OPERARIO en SU tarea: sube por su ruta propia (`/api/mis-ots/:idOt/lineas/:idLinea/evidencias`,
   * siempre INTERNA), no por la de gestión. Ausente = ruta de gestión (FASE 5.4).
   */
  readonly rutaPropia?: boolean;
  /**
   * F14.3 — sólo `ambito: 'PT'`: la intervención de la foto, si ya tenía id real al hacerla. Si se inició sin red,
   * `dependeDe` apunta a su `MNT_INICIAR` y la foto espera a que esa operación llegue al servidor.
   */
  readonly idIntervencion?: string | null;
}

/** F14.3 — los activos del parte tal como los conocía el dispositivo (el servidor detecta una baja posterior). */
export type ActivosConocidos = readonly { readonly id: string; readonly estado: string }[];

/** F14.3 — mismo contrato que `MNT_INICIAR` en `backend/src/mantenimiento/campo.ts` (`idEntidad` = parte). */
export interface PayloadMntIniciar {
  readonly activos: ActivosConocidos;
}

/** `idEntidad` = intervención (o el `idOperacionCliente` de su `MNT_INICIAR` como marcador, con `dependeDe`). */
export interface PayloadMntNota {
  readonly tipo: 'DIAGNOSTICO' | 'TRABAJO' | 'OBSERVACION';
  readonly texto: string;
}

/** `idEntidad` = parte. Una intención: el ledger decide al sincronizar (nunca se resta stock en el dispositivo). */
export interface PayloadMntConsumo {
  readonly idAlmacen: string;
  readonly idArticulo: string;
  readonly idLote: string | null;
  readonly idUnidad: string;
  readonly cantidad: string;
  /** Intervención real, o el marcador de su `MNT_INICIAR` (con `dependeDe`); `null` = sin intervención. */
  readonly idIntervencion: string | null;
  readonly observaciones: string | null;
}

/** `idEntidad` = intervención (o marcador). `FINALIZAR_Y_CERRAR` nunca sin red (lo rechaza el servidor). */
export interface PayloadMntFinalizar {
  readonly trabajoRealizado: string;
  readonly resultado: 'RESUELTO' | 'NO_RESUELTO' | 'PARCIAL';
  readonly decision: 'CONTINUAR' | 'PENDIENTE' | 'FINALIZAR';
  readonly motivoPendiente: string | null;
  readonly detallePendiente: string | null;
  readonly activos: ActivosConocidos;
}

export type PayloadMutacion =
  | PayloadLineaConfirmar
  | PayloadLineaVerificar
  | PayloadLineaMotivo
  | PayloadNcCrear
  | PayloadNcIniciarTratamiento
  | PayloadNcCerrar
  | PayloadEvidenciaSubir
  | PayloadMntIniciar
  | PayloadMntNota
  | PayloadMntConsumo
  | PayloadMntFinalizar;

/** F14.3 — lo que el servidor registró para una operación de mantenimiento ya aplicada (`MntSync`). */
export interface ResultadoMntMutacion {
  readonly idPt: string;
  readonly estadoPt: string;
  readonly idIntervencion: string | null;
  readonly idConsumo: string | null;
  readonly idNota: string | null;
  /** La hora que quedó en el servidor (ya corregida y acotada); puede no ser la del dispositivo. */
  readonly horaEfectiva: string | null;
  readonly avisos: readonly string[];
}

/**
 * Resultado servidor ya reconciliado de una mutación NC (FASE 5.4 §7) —
 * `null` mientras no se resuelva. Es la ÚNICA persistencia del mapeo
 * `idOperacionCliente -> idNc real`: nunca un store aparte (`ncs` o
 * similar) — igual criterio que 5.2 ("evitar stores muertos salvo que haya
 * razón clara"), la propia cola YA es donde vive ese dato, sobrevive a un
 * recargo de página porque `mutaciones` es IndexedDB persistente.
 */
export interface ResultadoNcMutacion {
  readonly idNc: string;
  readonly codigo: string;
  readonly estado: string;
  /**
   * PRE-COMMIT REVIEW FASE 5.4 punto 1 — el "revisionBase NC" que dejó esta
   * mutación (el `id` de su fila en `nc_historico_estado`, expuesto por el
   * servidor como `revisionServidorActual`). Sin esto, una transición
   * POSTERIOR sobre la misma NC en otra sesión offline no tendría nada que
   * enviar como `revisionBase` — siempre `null` — y el servidor nunca
   * podría distinguir un cambio externo real (CONFLICTO) de una repetición
   * sin cambios.
   */
  readonly idHistorial: string;
}

export interface Mutacion {
  readonly idOperacionCliente: string;
  readonly tipo: TipoMutacion;
  /**
   * id de `ot_lineas` para los cuatro tipos de línea (5.2); id de `ots`
   * para `NC_CREAR`; id de `no_conformidades` — o, si la NC de la que
   * depende todavía no se sincronizó, el `idOperacionCliente` de ESA
   * mutación como marcador local (ver `dependeDe`) — para
   * `NC_INICIAR_TRATAMIENTO`/`NC_CERRAR`; id de la entidad (OT/línea/NC) a
   * la que se adjunta para `EVIDENCIA_SUBIR` (mismo marcador local si esa
   * entidad es una NC creada offline). Mismo criterio dual que ya usa el
   * backend (`sync/servicio.ts`, `procesarMutacionNc`).
   */
  readonly idEntidad: string;
  readonly idOt: string;
  readonly idCentro: string;
  readonly payload: PayloadMutacion;
  /**
   * Último `id` de `ot_linea_eventos` conocido EN EL MOMENTO de crear esta
   * mutación (copiado del paquete local, nunca recalculado después — FASE
   * 5.2 §6). `null` = la línea seguía PENDIENTE sin ningún evento todavía;
   * es la MISMA convención que ya usa `PaqueteLineaOffline.revisionBase`
   * (FASE 5.1) — nunca el literal `"0"`, para no inventar una segunda
   * representación de "sin eventos".
   */
  readonly revisionBase: string | null;
  /** ISO — el momento real del hecho, nunca recalculado al sincronizar. */
  readonly fechaEfectiva: string;
  /** ISO — cuándo se guardó localmente. Reloj del dispositivo: UX, nunca autoridad de auditoría. */
  readonly creadoLocalEn: string;
  /** Monotónico, de `meta.ultimoOrdenLocal` — el orden real cuando el reloj no es de fiar. */
  readonly ordenLocal: number;
  /**
   * `idOperacionCliente` de la mutación activa anterior sobre la MISMA
   * línea (FASE 5.3 §14) — `null` si esta acción no depende causalmente de
   * ninguna otra. El servidor lo usa para el rebase de `revisionBase` en
   * una cadena CONFIRMAR→VERIFICAR creada sin conexión (§12): sin esto, la
   * segunda mutación compararía contra una revisión ya obsoleta en cuanto
   * la primera se aplicara de verdad.
   */
  readonly dependeDe: string | null;
  readonly estadoSync: EstadoSyncMutacion;
  readonly intentos: number;
  /**
   * F14.1 — cuándo falló el último intento (reloj del dispositivo; sólo para espaciar reintentos automáticos,
   * `estados-sync.ts::esperaReintento`). Vive dentro del registro cifrado: sin cambio de esquema IndexedDB.
   * Ausente en operaciones anteriores a F14.1.
   */
  readonly ultimoIntentoEn?: string | null;
  readonly ultimoError: { readonly codigo: string; readonly mensaje: string } | null;
  /**
   * Detalle mínimo de un CONFLICTO (FASE 5.3 §23) — sólo se rellena cuando
   * `ultimoError.codigo === 'REVISION_CAMBIADA'`. `null` en cualquier otro
   * caso, incluida una mutación que nunca se sincronizó todavía.
   */
  readonly detalleConflicto?: { readonly revisionServidorActual: string | null; readonly estadoServidorActual: string } | null;
  /** Sólo para `NC_CREAR`/`NC_INICIAR_TRATAMIENTO`/`NC_CERRAR` ya `SINCRONIZADA` — ver `ResultadoNcMutacion`. `null`/ausente en cualquier otro caso. */
  readonly resultadoNc?: ResultadoNcMutacion | null;
  /** F14.3 — sólo para `MNT_*` ya `SINCRONIZADA`: ids reales (las notas, consumos y fotos posteriores los usan). */
  readonly resultadoMnt?: ResultadoMntMutacion | null;
  /**
   * FASE 5.5 §2/§16 — por qué esta mutación está EN_ESPERA, sin inventar un
   * estado principal nuevo: `CONFLICTO_REVISION` = el servidor tiene una
   * revisión distinta a la que este dispositivo conocía (`detalleConflicto`
   * != null, resultado `CONFLICTO` de `/api/sync`); `DEPENDENCIA_BLOQUEADA`
   * = la mutación de la que depende (`dependeDe`) nunca llegó a aplicarse
   * (resultado `OMITIDA`) — la línea/NC de la que depende puede ni existir
   * todavía en el servidor, así que no hay nada con qué comparar revisión.
   * `undefined`/`null` en cualquier mutación que no esté EN_ESPERA, y en
   * filas EN_ESPERA guardadas antes de 5.5 (upgrade sin migración de filas).
   */
  readonly razonEspera?: 'CONFLICTO_REVISION' | 'DEPENDENCIA_BLOQUEADA' | null;
  /**
   * F14.2 — cómo se llama esta acción para quien la hizo («Confirmar limpieza · Habitación 305»), fijado al
   * registrarla con lo que se veía en pantalla. Sólo presentación (centro de sincronización, revisión); ausente en
   * operaciones anteriores (se usa la etiqueta del tipo).
   */
  readonly rotuloLocal?: string | null;
  /**
   * F14.2 — estado PROYECTADO de la línea cuando se registró esta acción (lo que el dispositivo creía). Con REHACER,
   * CONFIRMAR ya puede partir de PENDIENTE o de NO_OK: sin esto la revisión de un conflicto no sabría cuál fue.
   * Ausente en operaciones anteriores (entonces CONFIRMAR sólo podía partir de PENDIENTE).
   */
  readonly estadoOrigenLocal?: string | null;
}

// ==================================================== historial de resolución (FASE 5.5 §15)

export type AccionResolucionConflicto = 'DESCARTADA' | 'REINTENTADA' | 'REQUIERE_CONSULTOR';

/**
 * Un registro de "qué se hizo con este conflicto" — nunca auditoría de
 * servidor (encargo §15: "no hace falta auditoría empresarial nueva"),
 * sólo el rastro LOCAL de por qué una mutación desapareció de la bandeja de
 * pendientes. Retención acotada — ver `LIMITE_HISTORIAL_RESOLUCION`.
 */
export interface HistorialResolucion {
  readonly id: string;
  readonly idOperacionClienteOriginal: string;
  readonly tipo: TipoMutacion;
  readonly idEntidad: string;
  readonly idOt: string;
  readonly accion: AccionResolucionConflicto;
  readonly resueltoEn: string;
  /** El nuevo `idOperacionCliente` que sustituye al original — sólo para `REINTENTADA`. */
  readonly idOperacionClienteNueva: string | null;
}

const LIMITE_HISTORIAL_RESOLUCION = 50;

function promesa<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Petición IndexedDB fallida.'));
  });
}

function promesaTx(tx: IDBTransaction): Promise<void> {
  //  F14.1 — toda escritura en la cola avisa al indicador global y al centro de sincronización.
  const tocaCola = tx.mode === 'readwrite' && tx.objectStoreNames.contains('mutaciones');
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      resolve();
      if (tocaCola) avisarCambioCola();
    };
    tx.onerror = () => reject(tx.error ?? new Error('Transacción IndexedDB fallida.'));
    tx.onabort = () => reject(tx.error ?? new Error('Transacción IndexedDB abortada.'));
  });
}

export function abrirDb(idPerfil: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const peticion = indexedDB.open(nombreBaseDeDatosDePerfil(idPerfil), VERSION_ESQUEMA);
    peticion.onupgradeneeded = () => {
      const db = peticion.result;
      //  Sólo los campos que hacen falta como keyPath/índice quedan en
      //  claro — ver cabecera del fichero. Nunca `estado`/`idCentro` como
      //  índice de `ots`: no se consultan por ahí en ningún sitio del
      //  código (auditoría FASE 5.6 §2) y encriptarlos habría exigido
      //  mantener un índice inútil sobre un campo que ya no es legible.
      db.createObjectStore('meta', { keyPath: 'clave' });
      db.createObjectStore('ots', { keyPath: 'id' });
      db.createObjectStore('lineas', { keyPath: 'id' }).createIndex('idOt', 'idOt');
      db.createObjectStore('documentos', { keyPath: 'id' }).createIndex('idOt', 'idOt');
      db.createObjectStore('blobs', { keyPath: 'hash' });
      const mutaciones = db.createObjectStore('mutaciones', { keyPath: 'idOperacionCliente' });
      mutaciones.createIndex('estadoSync', 'estadoSync');
      mutaciones.createIndex('idOt', 'idOt');
      mutaciones.createIndex('idEntidad', 'idEntidad');
      db.createObjectStore('evidenciaBlobs', { keyPath: 'idOperacionCliente' });
      db.createObjectStore('historialResolucion', { keyPath: 'id' }).createIndex('resueltoEn', 'resueltoEn');
    };
    peticion.onsuccess = () => resolve(peticion.result);
    peticion.onerror = () => reject(peticion.error ?? new Error('No se ha podido abrir el almacén offline.'));
  });
}

/**
 * F14.1 — cuántas operaciones hay en cada estado SIN descifrar nada: `estadoSync` es un índice en claro (ver la
 * cabecera). Sirve con el perfil bloqueado (indicador global, aviso al cerrar sesión): nunca lee contenido de negocio.
 */
export interface RecuentoCola {
  readonly pendientes: number;
  readonly errores: number;
  readonly necesitanRevision: number;
}

export async function contarColaSinDescifrar(idPerfil: string): Promise<RecuentoCola> {
  const db = await abrirDb(idPerfil);
  try {
    const indice = db.transaction('mutaciones', 'readonly').objectStore('mutaciones').index('estadoSync');
    const n = async (e: EstadoSyncMutacion): Promise<number> => promesa(indice.count(e));
    const [pendientes, sincronizando, errores, espera, rechazadas] = await Promise.all(
      [n('PENDIENTE'), n('SINCRONIZANDO'), n('ERROR_REINTENTABLE'), n('EN_ESPERA'), n('RECHAZADA')],
    );
    return { pendientes: pendientes + sincronizando, errores, necesitanRevision: espera + rechazadas };
  } finally {
    db.close();
  }
}

// --------------------------------------------------- empaquetado cifrado

/** Fila física de un store "normal" (todo salvo `blobs`/`evidenciaBlobs`, que tienen su propio formato — ver más abajo). */
interface FilaCifrada {
  readonly cifrado: EnvelopeCifrado;
  readonly [campoEnClaro: string]: unknown;
}

async function empaquetar<T extends object>(
  idPerfil: string,
  store: string,
  claveLogica: string,
  valor: T,
  camposEnClaro: Record<string, unknown>,
): Promise<FilaCifrada> {
  const dek = dekActivaOLanzar(idPerfil);
  const cifrado = await cifrarRegistro(dek, idPerfil, store, claveLogica, valor);
  return { ...camposEnClaro, cifrado };
}

async function desempaquetar<T>(idPerfil: string, store: string, claveLogica: string, fila: FilaCifrada): Promise<T> {
  const dek = dekActivaOLanzar(idPerfil);
  return descifrarRegistro<T>(dek, idPerfil, store, claveLogica, fila.cifrado);
}

async function desempaquetarTodas<T>(idPerfil: string, store: string, claveDe: (f: FilaCifrada) => string, filas: FilaCifrada[]): Promise<T[]> {
  return Promise.all(filas.map((f) => desempaquetar<T>(idPerfil, store, claveDe(f), f)));
}

export async function leerMeta(idPerfil: string): Promise<MetaOffline | null> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('meta', 'readonly');
    const fila = (await promesa(tx.objectStore('meta').get(CLAVE_META))) as FilaCifrada | undefined;
    if (fila === undefined) return null;
    return desempaquetar<MetaOffline>(idPerfil, 'meta', CLAVE_META, fila);
  } finally {
    db.close();
  }
}

async function hashesConBlob(tx: IDBTransaction, hashes: readonly string[]): Promise<Set<string>> {
  //  Sólo comprueba EXISTENCIA de la clave — nunca hace falta descifrar
  //  para saber si un hash ya tiene blob guardado.
  const store = tx.objectStore('blobs');
  const presentes = new Set<string>();
  await Promise.all(
    [...new Set(hashes)].map(async (h) => {
      const fila = await promesa(store.get(h));
      if (fila !== undefined) presentes.add(h);
    }),
  );
  return presentes;
}

/**
 * Reemplaza el paquete completo por el que acaba de llegar del servidor.
 * Consciente de la cola pendiente — ver cabecera del fichero. Los
 * documentos cuyo hash YA está en `blobs` (de una sincronización anterior)
 * se marcan `disponibleOffline` sin volver a descargar nada.
 *
 * DOS TRANSACCIONES, NO UNA (§5/§20 — restricción de plataforma, no de
 * diseño): una transacción IndexedDB se auto-cierra en cuanto el control
 * vuelve al bucle de eventos sin ninguna petición IDB pendiente —
 * `crypto.subtle` NUNCA es una petición IDB, así que un `await` a
 * `cifrarRegistro`/`descifrarRegistro` en mitad de una transacción la deja
 * inactiva antes de la siguiente escritura (`TransactionInactiveError`,
 * confirmado también contra `fake-indexeddb`, que replica el algoritmo
 * real). La solución estándar —y la que sigue todo este fichero desde
 * 5.6— es leer y descifrar TODO lo que hace falta ANTES de abrir la
 * transacción de escritura, cifrar las filas nuevas también ANTES, y que
 * la transacción de escritura en sí sólo contenga llamadas síncronas
 * (`put`/`clear`/`delete`). La ATOMICIDAD de la escritura en sí —la
 * propiedad que de verdad importa: nunca "media OT nueva y media
 * antigua"— se conserva intacta, porque esa segunda transacción sigue
 * siendo una única transacción `readwrite`.
 */
export async function reemplazarPaquete(idPerfil: string, paquete: PaqueteOffline): Promise<void> {
  dekActivaOLanzar(idPerfil);
  const db = await abrirDb(idPerfil);
  try {
    //  FASE DE LECTURA — sólo peticiones IDB, ningún cifrado todavía.
    let filasMutaciones: FilaCifrada[];
    let filasOtsTodas: FilaCifrada[];
    let filasLineasTodas: FilaCifrada[];
    let filaMetaAnteriorCruda: FilaCifrada | undefined;
    let filaMantenimientoAnterior: FilaCifrada | undefined;
    let disponibles: Set<string>;
    const documentosMnt = documentosLocalesDeMantenimiento(paquete.mantenimiento ?? null);
    {
      const txLectura = db.transaction(['meta', 'ots', 'lineas', 'blobs', 'mutaciones'], 'readonly');
      disponibles = await hashesConBlob(
        txLectura,
        [...paquete.documentos, ...documentosMnt].map((d) => d.hashSha256),
      );
      filaMantenimientoAnterior = (await promesa(txLectura.objectStore('meta').get(CLAVE_MANTENIMIENTO))) as FilaCifrada | undefined;
      filasMutaciones = (await promesa(txLectura.objectStore('mutaciones').getAll())) as FilaCifrada[];
      filasOtsTodas = (await promesa(txLectura.objectStore('ots').getAll())) as FilaCifrada[];
      filasLineasTodas = (await promesa(txLectura.objectStore('lineas').getAll())) as FilaCifrada[];
      filaMetaAnteriorCruda = (await promesa(txLectura.objectStore('meta').get(CLAVE_META))) as FilaCifrada | undefined;
    }

    //  FASE DE DESCIFRADO/CÓMPUTO — sin transacción abierta.
    const todasLasMutaciones = await desempaquetarTodas<Mutacion>(
      idPerfil,
      'mutaciones',
      (f) => f['idOperacionCliente'] as string,
      filasMutaciones,
    );
    //  §18/§19 (FASE 5.2/5.3) — qué OT/línea hay que conservar aunque el
    //  paquete nuevo ya no las traiga: las que tienen al menos una
    //  mutación SIN RESOLVER — no sólo PENDIENTE (5.2), también
    //  EN_ESPERA/RECHAZADA/ERROR_REINTENTABLE (5.3 §19: "el refresh nunca
    //  borra" ninguno de esos cuatro estados). SINCRONIZADA es la única
    //  que ya no necesita conservar nada: lo que decía ya quedó aplicado
    //  de verdad en el servidor, y el paquete fresco lo refleja.
    const sinResolver = todasLasMutaciones.filter((m) => m.estadoSync !== 'SINCRONIZADA');
    const idsOtAConservar = new Set(sinResolver.map((m) => m.idOt));
    const idsLineaAConservar = new Set(sinResolver.map((m) => m.idEntidad));
    const idsOtNuevos = new Set(paquete.ots.map((o) => o.id));
    const idsLineaNuevas = new Set(paquete.lineas.map((l) => l.id));

    const otsAConservar: OtOfflineLocal[] = [];
    for (const filaVieja of filasOtsTodas) {
      const idOt = filaVieja['id'] as string;
      if (!idsOtAConservar.has(idOt) || idsOtNuevos.has(idOt)) continue; // ya llega fresca, o nadie la necesita — no hace falta conservar la copia vieja
      const vieja = await desempaquetar<OtOfflineLocal>(idPerfil, 'ots', idOt, filaVieja);
      otsAConservar.push({ ...vieja, fueraDeVentana: true });
    }
    const lineasAConservar: LineaOfflineLocal[] = [];
    for (const filaVieja of filasLineasTodas) {
      const idLinea = filaVieja['id'] as string;
      if (!idsLineaAConservar.has(idLinea) || idsLineaNuevas.has(idLinea)) continue;
      const vieja = await desempaquetar<LineaOfflineLocal>(idPerfil, 'lineas', idLinea, filaVieja);
      lineasAConservar.push({ ...vieja, fueraDeVentana: true });
    }

    //  F14.3 — el mismo criterio para los partes del técnico: uno con operaciones sin resolver se conserva (marcado
    //  `fueraDeVentana`) aunque el servidor ya no lo mande (reasignado, anulado…), para poder revisar su conflicto.
    let mantenimiento: MantenimientoLocal | null = null;
    if (paquete.mantenimiento != null) {
      const idsPartesNuevos = new Set(paquete.mantenimiento.partes.map((p) => p.parte.id));
      const anterior = filaMantenimientoAnterior === undefined
        ? null
        : await desempaquetar<MantenimientoLocal>(idPerfil, 'meta', CLAVE_MANTENIMIENTO, filaMantenimientoAnterior);
      const conservados = (anterior?.partes ?? [])
        .filter((p) => idsOtAConservar.has(p.parte.id) && !idsPartesNuevos.has(p.parte.id))
        .map((p) => ({ ...p, fueraDeVentana: true }));
      mantenimiento = {
        clave: CLAVE_MANTENIMIENTO,
        partes: [...paquete.mantenimiento.partes, ...conservados],
        stock: paquete.mantenimiento.stock,
      };
    }

    const metaAnterior =
      filaMetaAnteriorCruda === undefined
        ? undefined
        : await desempaquetar<MetaOffline>(idPerfil, 'meta', CLAVE_META, filaMetaAnteriorCruda);
    const meta: MetaOffline = {
      clave: CLAVE_META,
      rol: paquete.rol,
      generadoEn: paquete.generadoEn,
      ultimaSincronizacionEn: paquete.generadoEn,
      validoOperativamenteHasta: paquete.validoOperativamenteHasta,
      bloqueaTrabajoNuevoDesde: paquete.bloqueaTrabajoNuevoDesde ?? null,
      ventanaDesde: paquete.ventanaDesde,
      ventanaHasta: paquete.ventanaHasta,
      centros: paquete.centros,
      totalOts: paquete.mantenimiento != null ? paquete.mantenimiento.partes.length : paquete.ots.length,
      versionEsquemaLocal: VERSION_ESQUEMA,
      ultimoOrdenLocal: metaAnterior?.ultimoOrdenLocal ?? 0,
    };

    //  FASE DE CIFRADO — sin transacción abierta.
    const filasOtsNuevas = await Promise.all(paquete.ots.map((ot) => empaquetar(idPerfil, 'ots', ot.id, ot, { id: ot.id })));
    const filasLineasNuevas = await Promise.all(
      paquete.lineas.map((linea) => empaquetar(idPerfil, 'lineas', linea.id, linea, { id: linea.id, idOt: linea.idOt })),
    );
    const filasOtsConservadas = await Promise.all(
      otsAConservar.map((ot) => empaquetar(idPerfil, 'ots', ot.id, ot, { id: ot.id })),
    );
    const filasLineasConservadas = await Promise.all(
      lineasAConservar.map((linea) => empaquetar(idPerfil, 'lineas', linea.id, linea, { id: linea.id, idOt: linea.idOt })),
    );
    const filasDocumentosNuevas = await Promise.all(
      [...paquete.documentos, ...documentosMnt].map((doc) => {
        const local: DocumentoOfflineLocal = { ...doc, disponibleOffline: disponibles.has(doc.hashSha256) };
        return empaquetar(idPerfil, 'documentos', doc.id, local, { id: doc.id, idOt: doc.idOt });
      }),
    );
    const filaMetaNueva = await empaquetar(idPerfil, 'meta', CLAVE_META, meta, { clave: CLAVE_META });
    const filaMantenimientoNueva = mantenimiento === null
      ? null
      : await empaquetar(idPerfil, 'meta', CLAVE_MANTENIMIENTO, mantenimiento, { clave: CLAVE_MANTENIMIENTO });

    //  FASE DE ESCRITURA — únicamente llamadas síncronas, atómica.
    const txEscritura = db.transaction(['meta', 'ots', 'lineas', 'documentos'], 'readwrite');
    const otsStore = txEscritura.objectStore('ots');
    const lineasStore = txEscritura.objectStore('lineas');
    const documentosStore = txEscritura.objectStore('documentos');

    otsStore.clear();
    lineasStore.clear();
    documentosStore.clear();
    for (const fila of filasOtsNuevas) otsStore.put(fila);
    for (const fila of filasLineasNuevas) lineasStore.put(fila);
    for (const fila of filasOtsConservadas) otsStore.put(fila);
    for (const fila of filasLineasConservadas) lineasStore.put(fila);
    for (const fila of filasDocumentosNuevas) documentosStore.put(fila);
    txEscritura.objectStore('meta').put(filaMetaNueva);
    if (filaMantenimientoNueva === null) txEscritura.objectStore('meta').delete(CLAVE_MANTENIMIENTO);
    else txEscritura.objectStore('meta').put(filaMantenimientoNueva);

    await promesaTx(txEscritura);
  } finally {
    db.close();
  }
}

/** F14.3 — los documentos del paquete del técnico, en la forma común del store `documentos` (ver `DocumentoOfflineLocal`). */
function documentosLocalesDeMantenimiento(m: PaqueteMantenimiento | null): (PaqueteDocumentoOffline & { readonly idEvidenciaMnt: string })[] {
  if (m === null) return [];
  return m.documentos.map((d) => ({
    id: `${d.idPt}:${d.id}`,
    ambito: 'PT',
    idOt: d.idPt,
    idOtLinea: null,
    tipoDocumento: d.origen,
    titulo: d.titulo,
    codigo: '',
    version: 1,
    nombreOriginal: d.nombreOriginal,
    mime: d.mime,
    tamanoBytes: d.tamanoBytes,
    hashSha256: d.hashSha256,
    idEvidenciaMnt: d.id,
  }));
}

/** F14.3 — el paquete del técnico guardado en el dispositivo (`null` si este perfil no es de técnico). */
export async function leerMantenimientoLocal(idPerfil: string): Promise<MantenimientoLocal | null> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('meta', 'readonly');
    const fila = (await promesa(tx.objectStore('meta').get(CLAVE_MANTENIMIENTO))) as FilaCifrada | undefined;
    if (fila === undefined) return null;
    return await desempaquetar<MantenimientoLocal>(idPerfil, 'meta', CLAVE_MANTENIMIENTO, fila);
  } finally {
    db.close();
  }
}

/** F14.3 — documentos guardados de un parte (o de una OT): mismo índice `idOt`. */
export async function listarDocumentosLocalesDe(idPerfil: string, idOt: string): Promise<DocumentoOfflineLocal[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('documentos', 'readonly');
    const filas = (await promesa(tx.objectStore('documentos').index('idOt').getAll(idOt))) as FilaCifrada[];
    return await desempaquetarTodas<DocumentoOfflineLocal>(idPerfil, 'documentos', (f) => f['id'] as string, filas);
  } finally {
    db.close();
  }
}

export async function listarOtsLocal(idPerfil: string): Promise<OtOfflineLocal[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('ots', 'readonly');
    const filas = (await promesa(tx.objectStore('ots').getAll())) as FilaCifrada[];
    return desempaquetarTodas<OtOfflineLocal>(idPerfil, 'ots', (f) => f['id'] as string, filas);
  } finally {
    db.close();
  }
}

/** F14.2 — todas las líneas del paquete local, en `orden` (la jornada de «Mis trabajos» sin conexión). */
export async function listarLineasDeOtsLocal(idPerfil: string): Promise<LineaOfflineLocal[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('lineas', 'readonly');
    const filas = (await promesa(tx.objectStore('lineas').getAll())) as FilaCifrada[];
    const lineas = await desempaquetarTodas<LineaOfflineLocal>(idPerfil, 'lineas', (f) => f['id'] as string, filas);
    return lineas.sort((a, b) => a.orden - b.orden);
  } finally {
    db.close();
  }
}

export async function leerOtLocal(
  idPerfil: string,
  idOt: string,
): Promise<{ ot: OtOfflineLocal; lineas: LineaOfflineLocal[]; documentos: DocumentoOfflineLocal[] } | null> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction(['ots', 'lineas', 'documentos'], 'readonly');
    const filaOt = (await promesa(tx.objectStore('ots').get(idOt))) as FilaCifrada | undefined;
    if (filaOt === undefined) return null;
    const filasLineas = (await promesa(tx.objectStore('lineas').index('idOt').getAll(idOt))) as FilaCifrada[];
    const filasDocumentos = (await promesa(tx.objectStore('documentos').index('idOt').getAll(idOt))) as FilaCifrada[];

    const ot = await desempaquetar<OtOfflineLocal>(idPerfil, 'ots', idOt, filaOt);
    const lineas = await desempaquetarTodas<LineaOfflineLocal>(idPerfil, 'lineas', (f) => f['id'] as string, filasLineas);
    const documentos = await desempaquetarTodas<DocumentoOfflineLocal>(
      idPerfil,
      'documentos',
      (f) => f['id'] as string,
      filasDocumentos,
    );
    lineas.sort((a, b) => a.orden - b.orden);
    return { ot, lineas, documentos };
  } finally {
    db.close();
  }
}

export async function leerLineaLocal(idPerfil: string, idLinea: string): Promise<LineaOfflineLocal | null> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('lineas', 'readonly');
    const fila = (await promesa(tx.objectStore('lineas').get(idLinea))) as FilaCifrada | undefined;
    if (fila === undefined) return null;
    return desempaquetar<LineaOfflineLocal>(idPerfil, 'lineas', idLinea, fila);
  } finally {
    db.close();
  }
}

/** Documentos sin blob todavía — lo que `offline/paquete.ts` tiene que intentar descargar. */
export async function documentosPendientesDeBlob(idPerfil: string): Promise<DocumentoOfflineLocal[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('documentos', 'readonly');
    const filas = (await promesa(tx.objectStore('documentos').getAll())) as FilaCifrada[];
    const todos = await desempaquetarTodas<DocumentoOfflineLocal>(idPerfil, 'documentos', (f) => f['id'] as string, filas);
    return todos.filter((d) => !d.disponibleOffline);
  } finally {
    db.close();
  }
}

/**
 * Guarda el blob de un documento (cifrado — el contenido documental es
 * dato de negocio, §19/§21) y marca TODAS las filas de `documentos` que
 * comparten ese hash como disponibles — el mismo PDF puede estar aplicado
 * a varias OT del paquete (mismo documento maestro), y descargarlo una vez
 * basta para todas (encargo A17: "nunca descargar el mismo PDF N veces si
 * comparte hash"). El SHA-256 (§22) es del contenido EN CLARO, calculado
 * por quien llama antes de esta función — aquí sólo se cifra.
 */
export async function guardarBlobDocumento(idPerfil: string, hash: string, mime: string, blob: Blob): Promise<void> {
  const dek = dekActivaOLanzar(idPerfil);
  //  Cifrado del blob y de las filas de `documentos` a actualizar, ANTES
  //  de abrir la transacción de escritura — ver el porqué en el
  //  comentario de `reemplazarPaquete`.
  const cifradoBlob = await cifrarBlob(dek, idPerfil, 'blobs', hash, blob);

  const db = await abrirDb(idPerfil);
  try {
    const txLectura = db.transaction('documentos', 'readonly');
    const filas = (await promesa(txLectura.objectStore('documentos').getAll())) as FilaCifrada[];

    const documentos = await desempaquetarTodas<DocumentoOfflineLocal>(idPerfil, 'documentos', (f) => f['id'] as string, filas);
    const filasAActualizar = await Promise.all(
      documentos
        .filter((doc) => doc.hashSha256 === hash && !doc.disponibleOffline)
        .map((doc) => {
          const actualizado: DocumentoOfflineLocal = { ...doc, disponibleOffline: true };
          return empaquetar(idPerfil, 'documentos', doc.id, actualizado, { id: doc.id, idOt: doc.idOt });
        }),
    );

    const txEscritura = db.transaction(['blobs', 'documentos'], 'readwrite');
    txEscritura.objectStore('blobs').put({ hash, mime, cifrado: cifradoBlob });
    const documentosStore = txEscritura.objectStore('documentos');
    for (const fila of filasAActualizar) documentosStore.put(fila);
    await promesaTx(txEscritura);
  } finally {
    db.close();
  }
}

export async function leerBlobDocumento(idPerfil: string, hash: string): Promise<Blob | null> {
  const dek = dekActivaOLanzar(idPerfil);
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('blobs', 'readonly');
    const fila = (await promesa(tx.objectStore('blobs').get(hash))) as
      | { readonly hash: string; readonly mime: string; readonly cifrado: EnvelopeCifrado }
      | undefined;
    if (fila === undefined) return null;
    return descifrarBlob(dek, idPerfil, 'blobs', hash, fila.cifrado, fila.mime);
  } finally {
    db.close();
  }
}

/**
 * Purga los blobs de `blobs` que ningún documento del paquete actual
 * referencia ya — se llama DESPUÉS de `reemplazarPaquete`, nunca antes
 * (encargo B16/§21: purgar sólo lo que de verdad quedó sin referencia).
 * Devuelve cuántos blobs se eliminaron, sólo para poder probarlo.
 */
export async function purgarBlobsHuerfanos(idPerfil: string): Promise<number> {
  const db = await abrirDb(idPerfil);
  try {
    const txLectura = db.transaction(['blobs', 'documentos'], 'readonly');
    const filasDocumentos = (await promesa(txLectura.objectStore('documentos').getAll())) as FilaCifrada[];
    const hashesGuardados = (await promesa(txLectura.objectStore('blobs').getAllKeys())) as string[];

    const documentos = await desempaquetarTodas<DocumentoOfflineLocal>(
      idPerfil,
      'documentos',
      (f) => f['id'] as string,
      filasDocumentos,
    );
    const referenciados = new Set(documentos.map((d) => d.hashSha256));
    const aEliminar = hashesGuardados.filter((h) => !referenciados.has(h));

    const txEscritura = db.transaction('blobs', 'readwrite');
    const blobsStore = txEscritura.objectStore('blobs');
    for (const h of aEliminar) blobsStore.delete(h);
    await promesaTx(txEscritura);
    return aEliminar.length;
  } finally {
    db.close();
  }
}

// ------------------------------------------------------- evidencias offline (5.4)

/**
 * Guarda el blob de una evidencia pendiente de subir (cifrado — es
 * contenido de negocio), indexado por el `idOperacionCliente` de su
 * mutación `EVIDENCIA_SUBIR` — nunca por hash (a diferencia de `blobs`):
 * dos fotos nunca se deduplican entre sí, y cada una pertenece a
 * exactamente una mutación.
 */
export async function guardarBlobEvidencia(idPerfil: string, idOperacionCliente: string, blob: Blob): Promise<void> {
  const dek = dekActivaOLanzar(idPerfil);
  const cifrado = await cifrarBlob(dek, idPerfil, 'evidenciaBlobs', idOperacionCliente, blob);
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('evidenciaBlobs', 'readwrite');
    tx.objectStore('evidenciaBlobs').put({ idOperacionCliente, mime: blob.type, cifrado });
    await promesaTx(tx);
  } finally {
    db.close();
  }
}

export async function leerBlobEvidencia(idPerfil: string, idOperacionCliente: string): Promise<Blob | null> {
  const dek = dekActivaOLanzar(idPerfil);
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('evidenciaBlobs', 'readonly');
    const fila = (await promesa(tx.objectStore('evidenciaBlobs').get(idOperacionCliente))) as
      | { readonly idOperacionCliente: string; readonly mime: string; readonly cifrado: EnvelopeCifrado }
      | undefined;
    if (fila === undefined) return null;
    return descifrarBlob(dek, idPerfil, 'evidenciaBlobs', idOperacionCliente, fila.cifrado, fila.mime);
  } finally {
    db.close();
  }
}

/**
 * Borra el blob local — SÓLO se llama tras un resultado APLICADA/YA_APLICADA
 * confirmado por el servidor (FASE 5.4 §16: nunca en un estado ambiguo o de
 * fallo, para no perder la única copia de una foto que todavía podría hacer
 * falta reintentar).
 */
export async function eliminarBlobEvidencia(idPerfil: string, idOperacionCliente: string): Promise<void> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('evidenciaBlobs', 'readwrite');
    tx.objectStore('evidenciaBlobs').delete(idOperacionCliente);
    await promesaTx(tx);
  } finally {
    db.close();
  }
}

// ------------------------------------------------------- mutaciones (5.2)

/**
 * Inserta la mutación y avanza `meta.ultimoOrdenLocal` EN LA MISMA
 * transacción (encargo §4: "guardar mutación y actualizar contador en una
 * misma transacción IndexedDB") — nunca dos escrituras separadas que
 * pudieran desincronizarse. `idOperacionCliente` se genera aquí, una sola
 * vez (§3): quien llama nunca lo pasa ni lo regenera.
 *
 * Sin validación de negocio — eso vive en `offline/mutaciones.ts`, antes
 * de llamar aquí. Esta función asume que YA se decidió que la mutación es
 * válida; sólo persiste.
 *
 * F14.2 — única excepción a «se genera aquí»: `idOperacionCliente` puede venir dado cuando la MISMA acción ya se
 * intentó en línea y no hubo respuesta (`offline/ejecucion-propia.ts`). Reutilizarlo es lo que hace que el servidor
 * la reconozca (YA_APLICADA) si aquel intento sí llegó a aplicarse: nunca dos ejecuciones de la misma acción.
 */
/**
 * Serializa, por `idPerfil`, tareas que leen y escriben
 * `meta.ultimoOrdenLocal` (`insertarMutacion`/`reintentarMutacionEnEspera`)
 * — necesario porque, a diferencia de FASE 5.2-5.5, leer y escribir ya NO
 * puede ocurrir dentro de una única transacción IndexedDB atómica (ver
 * comentario de `reemplazarPaquete`): sin esto, dos llamadas concurrentes
 * en la misma pestaña (p.ej. un doble toque antes de que React deshabilite
 * el botón) podrían leer el mismo contador y escribir el mismo
 * `ordenLocal` dos veces. Nunca sustituye la atomicidad real de una
 * transacción — sólo evita la carrera dentro de ESTA pestaña; dos
 * pestañas distintas siguen sin coordinarse entre sí (igual límite que ya
 * tenía el mutex de `sync.ts`, `vueloEnCurso`).
 */
const colaPorPerfil = new Map<string, Promise<unknown>>();
function serializarPorPerfil<T>(idPerfil: string, tarea: () => Promise<T>): Promise<T> {
  const anterior = colaPorPerfil.get(idPerfil) ?? Promise.resolve();
  const siguiente = anterior.then(tarea, tarea);
  colaPorPerfil.set(
    idPerfil,
    siguiente.then(
      () => undefined,
      () => undefined,
    ),
  );
  return siguiente;
}

export async function insertarMutacion(
  idPerfil: string,
  datos: Pick<Mutacion, 'tipo' | 'idEntidad' | 'idOt' | 'idCentro' | 'payload' | 'revisionBase' | 'fechaEfectiva' | 'dependeDe'>
    & {
      readonly idOperacionCliente?: string;
      readonly rotuloLocal?: string | null;
      readonly estadoOrigenLocal?: string | null;
      /**
       * F14.3 — la operación YA se aplicó en línea (`offline/ejecucion-mantenimiento.ts`): se anota SINCRONIZADA con su
       * resultado, para que lo siguiente que se haga sin red parta de ella (p. ej. una nota sobre la intervención
       * recién iniciada) aunque el paquete todavía no se haya vuelto a descargar. Nunca se reenvía.
       */
      readonly aplicadaEnServidor?: ResultadoMntMutacion;
    },
): Promise<Mutacion> {
  dekActivaOLanzar(idPerfil);
  return serializarPorPerfil(idPerfil, async () => {
    const db = await abrirDb(idPerfil);
    try {
      //  LECTURA
      const txLectura = db.transaction('meta', 'readonly');
      const filaMeta = (await promesa(txLectura.objectStore('meta').get(CLAVE_META))) as FilaCifrada | undefined;
      if (filaMeta === undefined) throw new Error('insertarMutacion: no hay paquete local todavía.');

      //  DESCIFRADO + CÓMPUTO
      const metaActual = await desempaquetar<MetaOffline>(idPerfil, 'meta', CLAVE_META, filaMeta);
      const ordenLocal = metaActual.ultimoOrdenLocal + 1;
      const { aplicadaEnServidor, ...resto } = datos;
      const mutacion: Mutacion = {
        ...resto,
        idOperacionCliente: datos.idOperacionCliente ?? crypto.randomUUID(),
        ordenLocal,
        estadoSync: aplicadaEnServidor === undefined ? 'PENDIENTE' : 'SINCRONIZADA',
        intentos: 0,
        ultimoError: null,
        creadoLocalEn: new Date().toISOString(),
        ...(aplicadaEnServidor === undefined ? {} : { resultadoMnt: aplicadaEnServidor }),
      };

      //  CIFRADO
      const filaMutacion = await empaquetarMutacion(idPerfil, mutacion);
      const filaMetaNueva = await empaquetar(
        idPerfil,
        'meta',
        CLAVE_META,
        { ...metaActual, ultimoOrdenLocal: ordenLocal },
        { clave: CLAVE_META },
      );

      //  ESCRITURA — únicamente llamadas síncronas.
      const txEscritura = db.transaction(['mutaciones', 'meta'], 'readwrite');
      txEscritura.objectStore('mutaciones').put(filaMutacion);
      txEscritura.objectStore('meta').put(filaMetaNueva);
      await promesaTx(txEscritura);
      return mutacion;
    } finally {
      db.close();
    }
  });
}

/** Cifra una mutación completa con sus campos en claro — SIEMPRE antes de abrir la transacción de escritura (ver `reemplazarPaquete`). */
async function empaquetarMutacion(idPerfil: string, mutacion: Mutacion): Promise<FilaCifrada> {
  return empaquetar(idPerfil, 'mutaciones', mutacion.idOperacionCliente, mutacion, {
    idOperacionCliente: mutacion.idOperacionCliente,
    estadoSync: mutacion.estadoSync,
    idEntidad: mutacion.idEntidad,
    idOt: mutacion.idOt,
  });
}

async function leerMutacionDeStore(idPerfil: string, store: IDBObjectStore, idOperacionCliente: string): Promise<Mutacion | undefined> {
  const fila = (await promesa(store.get(idOperacionCliente))) as FilaCifrada | undefined;
  if (fila === undefined) return undefined;
  return desempaquetar<Mutacion>(idPerfil, 'mutaciones', idOperacionCliente, fila);
}

/** Mutaciones de una línea, en `ordenLocal` — la entrada de `proyectarEstadoLinea` (`offline/proyeccion.ts`). */
export async function listarMutacionesDeLinea(idPerfil: string, idLinea: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').index('idEntidad').getAll(idLinea))) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas.sort((a, b) => a.ordenLocal - b.ordenLocal);
  } finally {
    db.close();
  }
}

export async function leerMutacion(idPerfil: string, idOperacionCliente: string): Promise<Mutacion | null> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const m = await leerMutacionDeStore(idPerfil, tx.objectStore('mutaciones'), idOperacionCliente);
    return m ?? null;
  } finally {
    db.close();
  }
}

/** Mutaciones `NC_CREAR`/`NC_INICIAR_TRATAMIENTO`/`NC_CERRAR` de una OT, en `ordenLocal` — la proyección local de NC (`offline/proyeccion.ts`), igual criterio que `listarMutacionesDeLinea`: nunca un store `ncs` aparte. */
export async function listarMutacionesNcDeOt(idPerfil: string, idOt: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').index('idOt').getAll(idOt))) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas
      .filter((m): m is Mutacion => m.tipo === 'NC_CREAR' || m.tipo === 'NC_INICIAR_TRATAMIENTO' || m.tipo === 'NC_CERRAR')
      .sort((a, b) => a.ordenLocal - b.ordenLocal);
  } finally {
    db.close();
  }
}

/** F14.2 — todas las operaciones locales de una OT, en cualquier estado: «trabajo iniciado» (`caducidad.ts`). */
export async function listarMutacionesDeOt(idPerfil: string, idOt: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').index('idOt').getAll(idOt))) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas.sort((a, b) => a.ordenLocal - b.ordenLocal);
  } finally {
    db.close();
  }
}

/** Mutaciones `EVIDENCIA_SUBIR` de una OT, en `ordenLocal`. */
export async function listarMutacionesEvidenciaDeOt(idPerfil: string, idOt: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').index('idOt').getAll(idOt))) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas.filter((m) => m.tipo === 'EVIDENCIA_SUBIR').sort((a, b) => a.ordenLocal - b.ordenLocal);
  } finally {
    db.close();
  }
}

export async function listarMutacionesPendientes(idPerfil: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').index('estadoSync').getAll('PENDIENTE'))) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas.sort((a, b) => a.ordenLocal - b.ordenLocal);
  } finally {
    db.close();
  }
}

export async function contarMutacionesPendientes(idPerfil: string): Promise<number> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    return await promesa(tx.objectStore('mutaciones').index('estadoSync').count('PENDIENTE'));
  } finally {
    db.close();
  }
}

/**
 * FASE 5.7-C C03 — TODA mutación que todavía no ha terminado su ciclo
 * (cualquier `estadoSync` salvo `SINCRONIZADA`), sea cual sea su tipo o su
 * OT. Única fuente para el resumen "Cambios pendientes" de `/offline`: en
 * vez de que esa pantalla combine cuatro lecturas distintas
 * (`contarMutacionesPendientes`/`listarMutacionesSincronizables`/
 * `listarMutacionesEnEspera`/`listarMutacionesRechazadas`) para armar sus
 * propios contadores, deriva todos ellos de ESTA misma lista.
 */
export async function listarMutacionesNoSincronizadas(idPerfil: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').getAll())) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas.filter((m) => m.estadoSync !== 'SINCRONIZADA').sort((a, b) => a.ordenLocal - b.ordenLocal);
  } finally {
    db.close();
  }
}

/**
 * Descarta una mutación — sólo mientras siga PENDIENTE (encargo §13/§14:
 * nunca se edita en sitio, y nunca se descarta una ya en curso de
 * sincronizar o ya resuelta). Devuelve `false`, sin lanzar, si no existe o
 * ya no está en ese estado — quien llama decide qué decir al usuario.
 */
export async function descartarMutacion(idPerfil: string, idOperacionCliente: string): Promise<boolean> {
  const db = await abrirDb(idPerfil);
  try {
    const txLectura = db.transaction('mutaciones', 'readonly');
    const existente = await leerMutacionDeStore(idPerfil, txLectura.objectStore('mutaciones'), idOperacionCliente);
    if (existente === undefined || existente.estadoSync !== 'PENDIENTE') {
      return false;
    }
    const txEscritura = db.transaction('mutaciones', 'readwrite');
    txEscritura.objectStore('mutaciones').delete(idOperacionCliente);
    await promesaTx(txEscritura);
    return true;
  } finally {
    db.close();
  }
}

/**
 * Igual que `descartarMutacion`, pero para el otro extremo del ciclo de
 * vida: una mutación ya RESUELTA por el servidor con RECHAZADA (FASE 5.3
 * §24 — "puede mantenerse para consulta, o descartarse manualmente").
 * Nunca EN_ESPERA/ERROR_REINTENTABLE: esas siguen teniendo un camino hacia
 * adelante (reintento, o depende de otra), RECHAZADA no.
 */
export async function descartarMutacionRechazada(idPerfil: string, idOperacionCliente: string): Promise<boolean> {
  const db = await abrirDb(idPerfil);
  try {
    const txLectura = db.transaction('mutaciones', 'readonly');
    const existente = await leerMutacionDeStore(idPerfil, txLectura.objectStore('mutaciones'), idOperacionCliente);
    if (existente === undefined || existente.estadoSync !== 'RECHAZADA') {
      return false;
    }
    const txEscritura = db.transaction('mutaciones', 'readwrite');
    txEscritura.objectStore('mutaciones').delete(idOperacionCliente);
    await promesaTx(txEscritura);
    return true;
  } finally {
    db.close();
  }
}

// --------------------------------------------------------- sincronización (5.3)

/** Mutaciones que tiene sentido ENVIAR en el próximo lote — PENDIENTE (nunca intentada) o ERROR_REINTENTABLE (fallo transitorio, se reintenta). Nunca EN_ESPERA/RECHAZADA/SINCRONIZADA: esas exigen una acción explícita, nunca un reintento automático (§7/§8/§13). */
export async function listarMutacionesSincronizables(idPerfil: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').getAll())) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas
      .filter((m) => m.estadoSync === 'PENDIENTE' || m.estadoSync === 'ERROR_REINTENTABLE')
      .sort((a, b) => a.ordenLocal - b.ordenLocal);
  } finally {
    db.close();
  }
}

export interface CambioResolucionMutacion {
  readonly estadoSync: EstadoSyncMutacion;
  readonly ultimoError?: { readonly codigo: string; readonly mensaje: string } | null;
  readonly detalleConflicto?: { readonly revisionServidorActual: string | null; readonly estadoServidorActual: string } | null;
  /** `true` para ERROR_REINTENTABLE (§8: "intentos += 1") — nunca para el resto de resultados. */
  readonly incrementarIntentos?: boolean;
  /** Sólo para una mutación NC resuelta APLICADA/YA_APLICADA (FASE 5.4 §7) — ver `ResultadoNcMutacion`. */
  readonly resultadoNc?: ResultadoNcMutacion | null;
  /** F14.3 — sólo para una operación `MNT_*` resuelta APLICADA/YA_APLICADA — ver `ResultadoMntMutacion`. */
  readonly resultadoMnt?: ResultadoMntMutacion | null;
  /** FASE 5.5 §2/§16 — sólo junto a `estadoSync: 'EN_ESPERA'`. Ver `Mutacion.razonEspera`. */
  readonly razonEspera?: 'CONFLICTO_REVISION' | 'DEPENDENCIA_BLOQUEADA' | null;
}

/**
 * Aplica a UNA mutación el resultado que devolvió `/api/sync` para ella
 * (FASE 5.3 §§7-10/13). Silenciosa si la mutación ya no existe (se
 * descartó localmente entre que se envió y llegó la respuesta) — quien
 * llama no necesita distinguir ese caso de un `await` normal.
 */
export async function resolverMutacionTrasSincronizacion(
  idPerfil: string,
  idOperacionCliente: string,
  cambio: CambioResolucionMutacion,
): Promise<void> {
  const db = await abrirDb(idPerfil);
  try {
    const txLectura = db.transaction('mutaciones', 'readonly');
    const actual = await leerMutacionDeStore(idPerfil, txLectura.objectStore('mutaciones'), idOperacionCliente);
    if (actual === undefined) return;

    const siguiente: Mutacion = {
      ...actual,
      estadoSync: cambio.estadoSync,
      ultimoError: cambio.ultimoError ?? null,
      detalleConflicto: cambio.detalleConflicto ?? null,
      intentos: cambio.incrementarIntentos === true ? actual.intentos + 1 : actual.intentos,
      ultimoIntentoEn: cambio.incrementarIntentos === true ? new Date().toISOString() : (actual.ultimoIntentoEn ?? null),
      resultadoNc: cambio.resultadoNc ?? actual.resultadoNc ?? null,
      resultadoMnt: cambio.resultadoMnt ?? actual.resultadoMnt ?? null,
      razonEspera: cambio.razonEspera ?? null,
    };
    const fila = await empaquetarMutacion(idPerfil, siguiente);

    const txEscritura = db.transaction('mutaciones', 'readwrite');
    txEscritura.objectStore('mutaciones').put(fila);
    await promesaTx(txEscritura);
  } finally {
    db.close();
  }
}

/**
 * Reconciliación directa del snapshot local de una línea tras un
 * APLICADA/YA_APLICADA (FASE 5.3 §9/§19) — nunca espera al siguiente
 * `reemplazarPaquete` completo para que la UI deje de mostrar el estado
 * viejo. Silenciosa si la línea ya no está en el paquete local (salió de
 * ventana y sin otras mutaciones que la conservaran).
 */
export async function actualizarLineaTrasSincronizacion(
  idPerfil: string,
  idLinea: string,
  cambio: { readonly estado: string; readonly revisionBase: string },
): Promise<void> {
  const db = await abrirDb(idPerfil);
  try {
    const txLectura = db.transaction('lineas', 'readonly');
    const fila = (await promesa(txLectura.objectStore('lineas').get(idLinea))) as FilaCifrada | undefined;
    if (fila === undefined) return;

    const actual = await desempaquetar<LineaOfflineLocal>(idPerfil, 'lineas', idLinea, fila);
    const siguiente: LineaOfflineLocal = { ...actual, estado: cambio.estado, revisionBase: cambio.revisionBase };
    const filaSiguiente = await empaquetar(idPerfil, 'lineas', idLinea, siguiente, { id: idLinea, idOt: siguiente.idOt });

    const txEscritura = db.transaction('lineas', 'readwrite');
    txEscritura.objectStore('lineas').put(filaSiguiente);
    await promesaTx(txEscritura);
  } finally {
    db.close();
  }
}

// ============================================ revisión humana de conflictos (FASE 5.5)

/** Todas las mutaciones EN_ESPERA del usuario — la bandeja "Necesita revisión" (§6), sin filtrar por OT/línea. */
export async function listarMutacionesEnEspera(idPerfil: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').index('estadoSync').getAll('EN_ESPERA'))) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas.sort((a, b) => a.ordenLocal - b.ordenLocal);
  } finally {
    db.close();
  }
}

/** Todas las mutaciones RECHAZADA del usuario (§17) — sección propia de la bandeja, nunca mezclada con EN_ESPERA. */
export async function listarMutacionesRechazadas(idPerfil: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').index('estadoSync').getAll('RECHAZADA'))) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas.sort((a, b) => a.ordenLocal - b.ordenLocal);
  } finally {
    db.close();
  }
}

/**
 * Resuelve el `idEntidad` REAL de una mutación NC encadenada offline por
 * `dependeDe` — mismo dualismo que `mutaciones.ts::resolverDestinoNc` y la
 * misma resolución que aplica `reintentarMutacionEnEspera` antes de
 * reintentar (ver su comentario): mientras `dependeDe !== null`, el
 * `idEntidad` guardado en la fila es sólo el marcador local de la
 * dependencia, nunca el id real. Para cualquier mutación SIN `dependeDe`
 * (línea siempre; NC ya resuelta), `idEntidad` ya es el id real y se
 * devuelve tal cual. Uso de sólo lectura (p.ej. antes de consultar
 * `/api/sync/no-conformidades/:id` para revisión de conflicto) — nunca
 * reescribe la fila original.
 */
export async function resolverIdEntidadReal(idPerfil: string, m: Pick<Mutacion, 'idEntidad' | 'dependeDe'>): Promise<string> {
  if (m.dependeDe === null) return m.idEntidad;
  const padre = await leerMutacion(idPerfil, m.dependeDe);
  return padre?.resultadoNc?.idNc ?? m.idEntidad;
}

/** Mutaciones locales que dependen DIRECTAMENTE de `idOperacionCliente` — para que la UI muestre, antes de confirmar, qué más se vería afectado (§7/§13: "ofrecer descarte", nunca en silencio). */
export async function mutacionesDependientesDe(idPerfil: string, idOperacionCliente: string): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').getAll())) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas.filter((m) => m.dependeDe === idOperacionCliente);
  } finally {
    db.close();
  }
}

/**
 * Cierra la cadena `dependeDe` completa a partir de `raizId` (incluida la
 * raíz) — BFS sobre las mutaciones YA cargadas en memoria, sin volver a
 * tocar IndexedDB por cada nivel.
 *
 * GUARDIA DE CICLOS (FASE 5.7-A §23) — la UI nunca puede crear un ciclo
 * (`reintentarMutacionEnEspera` siempre encadena hacia un `idOperacionCliente`
 * NUEVO, nunca hacia uno ya existente en la cadena), pero esta función no
 * puede asumirlo: unos datos corruptos en IndexedDB (tampering, un bug
 * futuro en otro sitio) con `M1.dependeDe=M2` y `M2.dependeDe=M1` haría que
 * un BFS sin marcar visitados nunca terminara — cada vuelta re-encola al
 * otro miembro del ciclo indefinidamente. `visitados` corta cualquier id ya
 * añadido a `resultado`, así que el guardado por si nunca hace falta en el
 * caso normal (un árbol real jamás repite un id) y sólo actúa como red de
 * seguridad ante datos que nunca deberían existir.
 */
function recolectarCascada(todas: readonly Mutacion[], raizId: string): Mutacion[] {
  const porDependeDe = new Map<string, Mutacion[]>();
  for (const m of todas) {
    if (m.dependeDe === null) continue;
    const lista = porDependeDe.get(m.dependeDe) ?? [];
    lista.push(m);
    porDependeDe.set(m.dependeDe, lista);
  }
  const raiz = todas.find((m) => m.idOperacionCliente === raizId);
  if (raiz === undefined) return [];
  const resultado: Mutacion[] = [raiz];
  const visitados = new Set<string>([raizId]);
  const pendientes = [raizId];
  while (pendientes.length > 0) {
    const actualId = pendientes.pop()!;
    for (const hijo of porDependeDe.get(actualId) ?? []) {
      if (visitados.has(hijo.idOperacionCliente)) continue; // ciclo corrupto — ya se contó, no se vuelve a recorrer
      visitados.add(hijo.idOperacionCliente);
      resultado.push(hijo);
      pendientes.push(hijo.idOperacionCliente);
    }
  }
  return resultado;
}

/** Cifra una entrada de historial — SIEMPRE antes de abrir la transacción de escritura (ver `reemplazarPaquete`). */
async function empaquetarHistorial(idPerfil: string, entrada: Omit<HistorialResolucion, 'id'>): Promise<FilaCifrada> {
  const fila: HistorialResolucion = { ...entrada, id: crypto.randomUUID() };
  return empaquetar(idPerfil, 'historialResolucion', fila.id, fila, { id: fila.id, resueltoEn: fila.resueltoEn });
}

/** Retención acotada (§15: "no crecer sin límite") — purga, dentro de la MISMA transacción, lo más antiguo por encima de `LIMITE_HISTORIAL_RESOLUCION`. */
async function limitarHistorial(store: IDBObjectStore): Promise<void> {
  const claves = (await promesa(store.index('resueltoEn').getAllKeys())) as string[];
  const exceso = claves.length - LIMITE_HISTORIAL_RESOLUCION;
  for (let i = 0; i < exceso; i += 1) {
    store.delete(claves[i]!);
  }
}

/** F14.1 — las últimas operaciones ya guardadas en el servidor (centro de sincronización), de la más reciente. */
export async function listarMutacionesSincronizadasRecientes(idPerfil: string, limite = 10): Promise<Mutacion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(tx.objectStore('mutaciones').index('estadoSync').getAll('SINCRONIZADA'))) as FilaCifrada[];
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    return todas.sort((a, b) => b.ordenLocal - a.ordenLocal).slice(0, limite);
  } finally {
    db.close();
  }
}

export async function listarHistorialResolucion(idPerfil: string): Promise<HistorialResolucion[]> {
  const db = await abrirDb(idPerfil);
  try {
    const tx = db.transaction('historialResolucion', 'readonly');
    const filas = (await promesa(tx.objectStore('historialResolucion').index('resueltoEn').getAll())) as FilaCifrada[];
    const todas = await desempaquetarTodas<HistorialResolucion>(idPerfil, 'historialResolucion', (f) => f['id'] as string, filas);
    return todas.reverse();
  } finally {
    db.close();
  }
}

export interface ResultadoDescarteRevision {
  readonly idOperacionCliente: string;
  /** `idOperacionCliente` de cada dependiente que se descartó EN CASCADA junto a la raíz (§13/§14) — vacío si no tenía ninguno. */
  readonly cascada: readonly string[];
}

/**
 * Descarta una mutación EN_ESPERA o RECHAZADA (§7/§17) — nunca PENDIENTE
 * (eso sigue siendo `descartarMutacion`, sin cascada: una PENDIENTE nunca
 * tuvo oportunidad de bloquear nada porque nunca llegó a intentarse) ni
 * ERROR_REINTENTABLE (todavía tiene un camino hacia adelante, §18).
 *
 * CASCADA (§13/§14): un dependiente que sólo conoce a esta mutación por su
 * `idOperacionCliente` local (todavía sin id real de servidor) pierde toda
 * base al descartarla — nunca puede resolverse por su cuenta, así que se
 * descarta CON ella, en la MISMA transacción. La UI debe llamar antes a
 * `mutacionesDependientesDe` para que el usuario vea y confirme la cascada
 * — esta función nunca decide en silencio, sólo ejecuta.
 *
 * Una evidencia (`EVIDENCIA_SUBIR`) descartada en la cascada también borra
 * su blob local: el usuario ya confirmó explícitamente que este cambio no
 * se envía (§7 — "no borrar blob sin confirmación explícita", y esto SÍ lo
 * es).
 */
export async function descartarMutacionRevision(
  idPerfil: string,
  idOperacionCliente: string,
): Promise<ResultadoDescarteRevision | null> {
  const db = await abrirDb(idPerfil);
  try {
    //  LECTURA
    const txLectura = db.transaction('mutaciones', 'readonly');
    const filas = (await promesa(txLectura.objectStore('mutaciones').getAll())) as FilaCifrada[];

    //  DESCIFRADO + CÓMPUTO
    const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
    const raiz = todas.find((m) => m.idOperacionCliente === idOperacionCliente);
    if (raiz === undefined || (raiz.estadoSync !== 'EN_ESPERA' && raiz.estadoSync !== 'RECHAZADA')) {
      return null;
    }
    const aDescartar = recolectarCascada(todas, idOperacionCliente);
    const ahora = new Date().toISOString();

    //  CIFRADO — una entrada de historial por cada mutación descartada.
    const filasHistorial = await Promise.all(
      aDescartar.map((m) =>
        empaquetarHistorial(idPerfil, {
          idOperacionClienteOriginal: m.idOperacionCliente,
          tipo: m.tipo,
          idEntidad: m.idEntidad,
          idOt: m.idOt,
          accion: 'DESCARTADA',
          resueltoEn: ahora,
          idOperacionClienteNueva: null,
        }),
      ),
    );

    //  ESCRITURA — únicamente llamadas síncronas.
    const txEscritura = db.transaction(['mutaciones', 'evidenciaBlobs', 'historialResolucion'], 'readwrite');
    const store = txEscritura.objectStore('mutaciones');
    const blobsStore = txEscritura.objectStore('evidenciaBlobs');
    const historialStore = txEscritura.objectStore('historialResolucion');
    for (const m of aDescartar) {
      store.delete(m.idOperacionCliente);
      if (m.tipo === 'EVIDENCIA_SUBIR') blobsStore.delete(m.idOperacionCliente);
    }
    for (const fila of filasHistorial) historialStore.put(fila);
    await limitarHistorial(historialStore);
    await promesaTx(txEscritura);
    return {
      idOperacionCliente,
      cascada: aDescartar.filter((m) => m.idOperacionCliente !== idOperacionCliente).map((m) => m.idOperacionCliente),
    };
  } finally {
    db.close();
  }
}


export interface DatosReintentoMutacion {
  readonly payload: PayloadMutacion;
  /** La revisión FRESCA, obtenida del servidor en el momento de reintentar (§8/§9) — nunca la `revisionBase` obsoleta de la mutación original. */
  readonly revisionBase: string | null;
  /** El usuario puede corregirla si el dominio actual lo permite (§21); si no la toca, es la misma que registró originalmente. */
  readonly fechaEfectiva: string;
}

export interface ResultadoReintentoMutacion {
  readonly mutacion: Mutacion;
  /** `idOperacionCliente` de los dependientes LOCALES re-encadenados hacia la nueva mutación (§14). */
  readonly reencadenados: readonly string[];
}

/**
 * Sustituye una mutación EN_ESPERA por una NUEVA, con NUEVO
 * `idOperacionCliente` y la `revisionBase` fresca que el usuario acaba de
 * revisar (§8/§9/§19) — la original NUNCA se edita en sitio ni cambia su
 * `revisionBase`: queda resuelta/sustituida, nunca "la misma operación".
 *
 * Sólo EN_ESPERA — nunca RECHAZADA (§17: eso no ofrece reintento
 * automático) ni cualquier otro estado. La UI decide CUÁNDO tiene sentido
 * ofrecer este camino (estado fresco del servidor sigue admitiendo la
 * transición, §9/§10) — esta función no vuelve a comprobar eso, confía en
 * que ya se hizo antes de llamar.
 *
 * RE-ENCADENADO DE DEPENDIENTES (§14): cualquier mutación local que
 * dependía de la ORIGINAL por `dependeDe` pasa a depender de la NUEVA, en
 * la MISMA transacción. Un dependiente que estaba EN_ESPERA sólo por
 * `DEPENDENCIA_BLOQUEADA` (nunca por su propio conflicto de revisión)
 * vuelve a PENDIENTE — ya tiene de nuevo un padre con camino hacia
 * adelante, así que el próximo intento de sincronización puede
 * reintentarlo sin que el usuario tenga que hacer nada más por él.
 */
export function reintentarMutacionEnEspera(
  idPerfil: string,
  idOperacionClienteOriginal: string,
  datos: DatosReintentoMutacion,
): Promise<ResultadoReintentoMutacion | null> {
  dekActivaOLanzar(idPerfil);
  return serializarPorPerfil(idPerfil, async () => {
    const db = await abrirDb(idPerfil);
    try {
      //  LECTURA
      const txLectura = db.transaction(['mutaciones', 'meta'], 'readonly');
      const filas = (await promesa(txLectura.objectStore('mutaciones').getAll())) as FilaCifrada[];
      const filaMeta = (await promesa(txLectura.objectStore('meta').get(CLAVE_META))) as FilaCifrada | undefined;
      if (filaMeta === undefined) throw new Error('reintentarMutacionEnEspera: no hay paquete local todavía.');

      //  DESCIFRADO + CÓMPUTO
      const todas = await desempaquetarTodas<Mutacion>(idPerfil, 'mutaciones', (f) => f['idOperacionCliente'] as string, filas);
      const original = todas.find((m) => m.idOperacionCliente === idOperacionClienteOriginal);
      if (original === undefined || original.estadoSync !== 'EN_ESPERA') return null;

      const metaActual = await desempaquetar<MetaOffline>(idPerfil, 'meta', CLAVE_META, filaMeta);
      const ordenLocal = metaActual.ultimoOrdenLocal + 1;

      //  Una transición NC encadenada offline (`dependeDe !== null`) guarda en
      //  `idEntidad` sólo el MARCADOR local (`idOperacionCliente` de la
      //  mutación de la que depende) hasta que esa dependencia se resuelve —
      //  mismo dualismo que `mutaciones.ts::resolverDestinoNc` — y la
      //  resolución nunca reescribe `idEntidad` en la fila original (§14: "no
      //  editar en sitio"). Por construcción, si esta mutación llegó a
      //  CONFLICTO es porque esa dependencia YA se aplicó (si no, habría sido
      //  OMITIDA, nunca CONFLICTO) — así que su `resultadoNc.idNc` es el id
      //  real que hay que usar aquí, nunca el marcador heredado.
      let idEntidadReal = original.idEntidad;
      if (original.dependeDe !== null) {
        const padre = todas.find((m) => m.idOperacionCliente === original.dependeDe);
        if (padre?.resultadoNc != null) idEntidadReal = padre.resultadoNc.idNc;
      }

      const nueva: Mutacion = {
        idOperacionCliente: crypto.randomUUID(),
        tipo: original.tipo,
        idEntidad: idEntidadReal,
        idOt: original.idOt,
        idCentro: original.idCentro,
        payload: datos.payload,
        revisionBase: datos.revisionBase,
        fechaEfectiva: datos.fechaEfectiva,
        creadoLocalEn: new Date().toISOString(),
        ordenLocal,
        dependeDe: null,
        estadoSync: 'PENDIENTE',
        intentos: 0,
        ultimoError: null,
      };

      const reencadenadas: Mutacion[] = [];
      const reencadenados: string[] = [];
      for (const dep of todas) {
        if (dep.dependeDe !== idOperacionClienteOriginal) continue;
        const eraBloqueadaPorEstaDependencia = dep.estadoSync === 'EN_ESPERA' && dep.razonEspera === 'DEPENDENCIA_BLOQUEADA';
        reencadenadas.push({
          ...dep,
          dependeDe: nueva.idOperacionCliente,
          estadoSync: eraBloqueadaPorEstaDependencia ? 'PENDIENTE' : dep.estadoSync,
          razonEspera: eraBloqueadaPorEstaDependencia ? null : (dep.razonEspera ?? null),
          ultimoError: eraBloqueadaPorEstaDependencia ? null : dep.ultimoError,
        });
        reencadenados.push(dep.idOperacionCliente);
      }

      //  CIFRADO
      const filaNueva = await empaquetarMutacion(idPerfil, nueva);
      const filasReencadenadas = await Promise.all(reencadenadas.map((m) => empaquetarMutacion(idPerfil, m)));
      const filaMetaNueva = await empaquetar(
        idPerfil,
        'meta',
        CLAVE_META,
        { ...metaActual, ultimoOrdenLocal: ordenLocal },
        { clave: CLAVE_META },
      );
      const filaHistorial = await empaquetarHistorial(idPerfil, {
        idOperacionClienteOriginal,
        tipo: original.tipo,
        idEntidad: original.idEntidad,
        idOt: original.idOt,
        accion: 'REINTENTADA',
        resueltoEn: nueva.creadoLocalEn,
        idOperacionClienteNueva: nueva.idOperacionCliente,
      });

      //  ESCRITURA — únicamente llamadas síncronas.
      const txEscritura = db.transaction(['mutaciones', 'meta', 'historialResolucion'], 'readwrite');
      const store = txEscritura.objectStore('mutaciones');
      store.put(filaNueva);
      store.delete(idOperacionClienteOriginal);
      for (const fila of filasReencadenadas) store.put(fila);
      txEscritura.objectStore('meta').put(filaMetaNueva);
      const historialStore = txEscritura.objectStore('historialResolucion');
      historialStore.put(filaHistorial);
      await limitarHistorial(historialStore);
      await promesaTx(txEscritura);

      return { mutacion: nueva, reencadenados };
    } finally {
      db.close();
    }
  });
}
